import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import classNames from 'classnames';
import { useCallback, useId, useMemo, useState } from 'react';
import { ChevronDownIcon, XIcon } from '@repo/icons';
import Popover from '../Popover/Popover';
import { useAnchoredPopover } from '../Popover/useAnchoredPopover';
import { getLabels } from '../labels';
const containerBaseClasses = 'relative flex w-full min-w-0 max-w-full gap-2 rounded-xl shadow-sm border overflow-hidden cursor-pointer';
const sizeClasses = {
    sm: 'px-2.5 py-2 text-sm',
    base: 'px-3 py-2.5 text-sm',
    lg: 'px-3.5 py-3 text-base',
    xl: 'px-4 py-3.5 text-base',
};
const stateClasses = {
    default: 'bg-secondary-medium border-stroke-base-medium text-body-subtle focus-within:bg-secondary-medium focus-within:border-brand focus-within:text-fg-brand',
    success: 'text-fg-success-strong border-stroke-success-subtle bg-success-soft',
    danger: 'text-fg-danger-strong border-stroke-danger-subtle bg-danger-soft',
};
const disabledClasses = 'bg-secondary-medium border-stroke-base-medium text-fg-disabled cursor-not-allowed';
const inlineBaseClasses = 'relative flex w-full min-w-0 max-w-full gap-2 cursor-pointer bg-transparent';
const inlineSizeClasses = {
    sm: 'py-2 text-sm',
    base: 'py-2.5 text-sm',
    lg: 'py-3 text-base',
    xl: 'py-3.5 text-base',
};
const optionBaseClasses = 'px-3 py-2 cursor-pointer text-sm';
export default function Dropdown({ testId, options, value, onChange, label, placeholder = '', size = 'base', state = 'default', disabled = false, clearable = false, onClear, leftIcon: LeftIcon, leftElement, className, longer = false, enablePortal = false, variant = 'default', }) {
    const dropdownId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const { clearAriaLabel } = getLabels().input;
    const handleClose = useCallback(() => setIsOpen(false), []);
    const { triggerRef: inlineTriggerRef, popoverRef: inlinePopoverRef } = useAnchoredPopover({
        isOpen: isOpen && !enablePortal,
        onClose: handleClose,
        expand: longer,
    });
    const selectedOption = useMemo(() => options.find((option) => option.value === value) ?? null, [options, value]);
    const showClearButton = useMemo(() => {
        if (!clearable || disabled)
            return false;
        return value !== undefined && value !== null;
    }, [clearable, disabled, value]);
    const handleToggle = () => {
        if (disabled)
            return;
        setIsOpen((previous) => !previous);
    };
    const handleSelect = (option) => {
        if (option.disabled)
            return;
        onChange?.(option.value);
        setIsOpen(false);
    };
    const handleClear = (event) => {
        event.stopPropagation();
        if (disabled)
            return;
        onChange?.(null);
        onClear?.();
        setIsOpen(false);
    };
    const handleTriggerKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggle();
        }
    };
    const isInline = variant === 'inline';
    const containerClasses = isInline
        ? classNames(inlineBaseClasses, 'items-center', inlineSizeClasses[size], disabled ? 'text-fg-disabled cursor-not-allowed' : 'text-body-subtle', className)
        : classNames(containerBaseClasses, 'items-center', sizeClasses[size], disabled ? disabledClasses : stateClasses[state], !disabled && 'focus-within:ring-2 focus-within:ring-brand', className);
    const leftContent = leftElement ?? (LeftIcon ? _jsx(LeftIcon, { className: "size-4" }) : null);
    const optionItems = options.map((option) => {
        const handleOptionClick = () => handleSelect(option);
        return (_jsx("li", { role: "option", "aria-selected": option.value === value, "aria-disabled": option.disabled, className: classNames(optionBaseClasses, option.disabled ? 'text-fg-disabled cursor-not-allowed' : 'text-body hover:bg-secondary-medium', option.value === value && 'bg-brand-soft text-fg-brand font-medium'), onClick: handleOptionClick, "data-testid": testId?.append('option')?.append(option.value), children: option.label }, option.value));
    });
    const renderTrigger = (triggerRef) => (_jsxs("div", { ref: triggerRef, id: dropdownId, role: "combobox", "aria-expanded": isOpen, "aria-haspopup": "listbox", tabIndex: disabled ? -1 : 0, className: containerClasses, onClick: handleToggle, onKeyDown: handleTriggerKeyDown, "data-testid": testId, children: [leftContent && _jsx("div", { className: "flex shrink-0 items-center justify-center", children: leftContent }), _jsx("span", { className: classNames('min-w-0 flex-1 bg-transparent wrap-break-word text-inherit', !selectedOption && 'text-body-subtle truncate'), children: selectedOption ? selectedOption.label : placeholder }), _jsxs("div", { className: "flex shrink-0 items-center gap-3", children: [showClearButton && (_jsx("button", { type: "button", "aria-label": clearAriaLabel, className: "cursor-pointer border-none bg-transparent p-0", onClick: handleClear, "data-testid": testId?.append('clear'), children: _jsx(XIcon, { size: 20 }) })), _jsx(ChevronDownIcon, { className: classNames('size-4 transition-transform', isOpen && 'rotate-180') })] })] }));
    return (_jsxs("div", { className: "flex flex-col", children: [label && (_jsx("label", { htmlFor: dropdownId, className: "text-heading mb-2.5 text-sm leading-5 font-medium", "data-testid": testId?.append('label'), children: label })), _jsx("div", { className: "relative", children: enablePortal ? (_jsx(Popover, { isOpen: isOpen, onClose: handleClose, renderTrigger: renderTrigger, expand: longer, matchTriggerWidth: true, testId: testId?.append('options'), children: _jsx("ul", { role: "listbox", className: "max-h-[inherit] overflow-y-auto", children: optionItems }) })) : (_jsxs(_Fragment, { children: [renderTrigger(inlineTriggerRef), isOpen && (_jsx("ul", { ref: inlinePopoverRef, role: "listbox", className: "bg-primary border-stroke-base-medium z-dropdown absolute mt-1 max-h-60 w-full overflow-y-auto rounded-xl border shadow-lg", "data-testid": testId?.append('options'), children: optionItems }))] })) })] }));
}
